import {
	KEY_LANG,
	KEY_SMART_VIEW,
	KEY_USER_COMPANY,
	KEY_USER_EMAIL,
	KEY_USER_ID,
	KEY_USER_DISPLAY_NAME,
	QUOTA_LIMIT_BANNER_DISABLED,
	HOME_TAB,
	HIDE_DOC_STATUS_INFO_BANNER,
	LOGGED_REAL_COMPANY,
	PRODUCT_TOURS,
	PRODUCT_TOUR_EXPLORATION,
	KEY_DOC_CENTER_TABLE_COLUMN_ORDER,
	KEY_DOC_CENTER_TABLE_COLUMN_WIDTH,
	KEY_PORTFOLIO_TABLE_COLUMN_ORDER,
} from "./keys/keys";
import { WebSocket } from "../../api";

class ContextProvider {
	#isLogged;

	constructor() {
		this.localStorage = window.localStorage;
		this.sessionStorage = window.sessionStorage;
		this.#isLogged = false;
		this.bearerToken = "";
	}

	login(token) {
		if (!token || !token.includes("Bearer")) {
			this.logout();
			return this.#isLogged;
		}
		this.#isLogged = true;
		this.bearerToken = token;
		return this.#isLogged;
	}

	logout() {
		this.#isLogged = false;
		this.bearerToken = "";
		this.clearStorage();
		this.sessionStorage.clear();
		WebSocket.disconnect();
	}

	isLogged() {
		return this.#isLogged;
	}

	getToken() {
		return this.bearerToken;
	}

	getLang() {
		return this.localStorage.getItem(KEY_LANG);
	}

	getCompany() {
		return this.localStorage.getItem(KEY_USER_COMPANY);
	}

	setLang(lang) {
		this.localStorage.setItem(KEY_LANG, lang ?? "en");
	}

	setUserDetails({ companyId, displayName, email, userId }) {
		this.localStorage.setItem(KEY_USER_EMAIL, email);
		this.localStorage.setItem(KEY_USER_DISPLAY_NAME, displayName);
		this.localStorage.setItem(KEY_USER_ID, userId);
		this.localStorage.setItem(KEY_USER_COMPANY, companyId);
	}

	setCompany(companyId) {
		this.localStorage.setItem(KEY_USER_COMPANY, companyId);
	}

	getCautionBannerDisabled() {
		return this.sessionStorage.getItem(QUOTA_LIMIT_BANNER_DISABLED);
	}

	setCautionBannerDisabled(isDisabled) {
		this.sessionStorage.setItem(QUOTA_LIMIT_BANNER_DISABLED, isDisabled);
	}

	getHideDocStatusInfoBanner() {
		return this.sessionStorage.getItem(HIDE_DOC_STATUS_INFO_BANNER);
	}

	setHideDocStatusInfoBanner(isDisabled) {
		this.sessionStorage.setItem(HIDE_DOC_STATUS_INFO_BANNER, isDisabled);
	}

	getHomeTab() {
		return this.localStorage.getItem(HOME_TAB);
	}

	setHomeTab(tab) {
		this.localStorage.setItem(HOME_TAB, tab);
	}

	getloggedPhysicalCompany() {
		const value = Number(this.localStorage.getItem(LOGGED_REAL_COMPANY));
		return value !== null && !Number.isNaN(value) ? value : null;
	}

	setloggedPhysicalCompany(compId) {
		this.localStorage.setItem(LOGGED_REAL_COMPANY, compId);
	}

	clearStorage() {
		this.sessionStorage.setItem(KEY_SMART_VIEW, null);
	}

	setProductTours(tours) {
		this.localStorage.setItem(PRODUCT_TOURS, JSON.stringify(tours));
	}

	getProductTours() {
		return this.localStorage.getItem(PRODUCT_TOURS);
	}

	setTourExploration(isSolo) {
		this.localStorage.setItem(PRODUCT_TOUR_EXPLORATION, JSON.stringify(isSolo));
	}

	getTourExploration() {
		const soloEploration = this.localStorage.getItem(PRODUCT_TOUR_EXPLORATION);
		try {
			const parsed = soloEploration ? JSON.parse(soloEploration) : false;
			return typeof parsed === "boolean" ? parsed : false;
		} catch {
			return false;
		}
	}

	setDocCenterTableColumnOrder(columnOrder) {
		this.localStorage.setItem(KEY_DOC_CENTER_TABLE_COLUMN_ORDER, JSON.stringify(columnOrder));
	}

	getDocCenterTableColumnOrder() {
		const tableColumns = this.localStorage.getItem(KEY_DOC_CENTER_TABLE_COLUMN_ORDER);
		try {
			const parsed = tableColumns ? JSON.parse(tableColumns) : undefined;
			return Array.isArray(parsed) ? parsed : undefined;
		} catch {
			return undefined;
		}
	}

	setDocCenterTableColumnWidth(columnWidth) {
		this.localStorage.setItem(KEY_DOC_CENTER_TABLE_COLUMN_WIDTH, JSON.stringify(columnWidth));
	}

	getDocCenterTableColumnWidth() {
		const tableColumns = this.localStorage.getItem(KEY_DOC_CENTER_TABLE_COLUMN_WIDTH);
		try {
			return tableColumns ? JSON.parse(tableColumns) : undefined;
		} catch {
			return undefined;
		}
	}

	setPortfolioTableColumnOrder(columnOrder) {
		this.localStorage.setItem(KEY_PORTFOLIO_TABLE_COLUMN_ORDER, JSON.stringify(columnOrder));
	}

	getPortfolioTableColumnOrder() {
		const tableColumns = this.localStorage.getItem(KEY_PORTFOLIO_TABLE_COLUMN_ORDER);
		try {
			const parsed = tableColumns ? JSON.parse(tableColumns) : undefined;
			if (!Array.isArray(parsed)) {
				return undefined;
			}
			const isValid = parsed.every(
				(col) =>
					col &&
					typeof col.key === "string" &&
					typeof col.name === "string" &&
					(col.visibility === undefined || typeof col.visibility === "boolean")
			);
			return isValid ? parsed : undefined;
		} catch {
			return undefined;
		}
	}
}

export default new ContextProvider();
